<template>
    <div>
        <div v-if="loading" class="text-center">
            <i class="fa fa-circle-o-notch fa-spin"></i> Loading
        </div>
        <div v-else>
            <vue-headful :title="'Custom Form Builder :: MeritEHS'"/>

            <h4 class="py-3 mb-4">
                <ol class="breadcrumb font-weight-bold m-0">
                    <li class="breadcrumb-item font-weight-light">
                        <router-link :to="`/custom-forms`">
                            <i class="fa fa-file-text"></i> Custom Forms
                        </router-link>
                    </li>
                    <template v-if="form.id">
                        <li class="breadcrumb-item font-weight-light">
                            <router-link :to="`/custom-forms/forms/view/${form.id}`">{{ form.covid_form_name }}</router-link>
                        </li>
                        <li class="breadcrumb-item active">
                            <i class="fa fa-edit"></i> Edit
                        </li>
                    </template>
                    <template v-else>
                        <li class="breadcrumb-item active">
                            Create
                        </li>
                    </template>
                </ol>
            </h4>

            <div class="mt-4">
                <div class="row">
                    <div class="col-lg-9 col-12">
                        <div class="card mb-4">
                            <div class="card-header">
                                <strong>Form Details</strong>
                            </div>
                            <div class="card-body">
                                <div class="form-group row">
                                    <label class="col-lg-3 col-sm-4 col-form-label text-sm-right" :class="{'text-muted': !errors.covid_form_name, 'text-danger': errors.covid_form_name}">Form Name:</label>
                                    <div class="col-lg-6 col-sm-8 pt-2">
                                        <b-form-input v-model="form.covid_form_name" placeholder="Enter a Name for this Form" :class="{'is-invalid': errors.covid_form_name}"></b-form-input>

                                        <div class="text-danger mt-2" v-if="errors.covid_form_name">
                                            <ul>
                                                <li v-for="err in errors.covid_form_name">{{ err }}</li>
                                            </ul>
                                        </div>
                                    </div>
                                </div>
                                <div class="form-group row">
                                    <label class="col-lg-3 col-sm-4 col-form-label text-sm-right" :class="{'text-muted': !errors.require_jobsite, 'text-danger': errors.require_jobsite}">Require Jobsite:</label>
                                    <div class="col-sm-8 pt-2">
                                        <b-form-checkbox v-model="form.require_jobsite" :value="1" :unchecked-value="0" plain></b-form-checkbox>

                                        <div class="text-danger mt-2" v-if="errors.require_jobsite">
                                            <ul>
                                                <li v-for="err in errors.require_jobsite">{{ err }}</li>
                                            </ul>
                                        </div>
                                    </div>
                                </div>
                                <div class="form-group row">
                                    <label class="col-lg-3 col-sm-4 col-form-label text-sm-right" :class="{'text-muted': !errors.enable_public_access, 'text-danger': errors.enable_public_access}">
                                        Enable Public Access:
                                    </label>
                                    <div class="col-sm-8 pt-2">
                                        <b-form-checkbox v-model="form.enable_public_access" :value="1" :unchecked-value="0" plain>
                                            If checked, a QR Code will be generated to provide scanning access. Employee# is required.
                                        </b-form-checkbox>

                                        <div class="text-danger mt-2" v-if="errors.enable_public_access">
                                            <ul>
                                                <li v-for="err in errors.enable_public_access">{{ err }}</li>
                                            </ul>
                                        </div>
                                    </div>
                                </div>
                                <div class="form-group row" v-if="form.enable_public_access == 1">
                                    <label class="col-lg-3 col-sm-4 col-form-label text-sm-right" :class="{'text-muted': !errors.enable_guest_access, 'text-danger': errors.enable_guest_access}">
                                        Enable Guest Access:
                                    </label>
                                    <div class="col-sm-8 pt-2">
                                        <b-form-checkbox v-model="form.enable_guest_access" :value="1" :unchecked-value="0" plain>
                                            If checked, people scanning the QR code will not be required to input their Employee# to access the form.
                                        </b-form-checkbox>

                                        <div class="text-danger mt-2" v-if="errors.enable_guest_access">
                                            <ul>
                                                <li v-for="err in errors.enable_guest_access">{{ err }}</li>
                                            </ul>
                                        </div>
                                    </div>
                                </div>

                                <div class="form-group row" v-if="form.enable_public_access == 1">
                                    <label class="col-lg-3 col-sm-4 col-form-label text-sm-right" :class="{'text-muted': !errors.req_public_form_password, 'text-danger': errors.req_public_form_password}">
                                        Require Access Password:
                                    </label>
                                    <div class="col-sm-8 pt-2">
                                        <b-form-checkbox v-model="form.req_public_form_password" :value="1" :unchecked-value="0" plain>
                                            If checked, people scanning the QR code will be required to enter the specified access password
                                        </b-form-checkbox>

                                        <div class="text-danger mt-2" v-if="errors.req_public_form_password">
                                            <ul>
                                                <li v-for="err in errors.req_public_form_password">{{ err }}</li>
                                            </ul>
                                        </div>
                                    </div>
                                </div>

                                <div class="form-group row" v-if="form.enable_public_access == 1 && form.req_public_form_password == 1">
                                    <label class="col-lg-3 col-sm-4 col-form-label text-sm-right" :class="{'text-muted': !errors.public_form_password, 'text-danger': errors.public_form_password}">
                                        Public Access Password
                                    </label>
                                    <div class="col-sm-8 pt-2">
                                        <b-form-input v-model="form.public_form_password" placeholder="Enter Public Access Password" :class="{'is-invalid': errors.public_form_password}" />

                                        <div class="text-danger mt-2" v-if="errors.public_form_password">
                                            <span v-for="err in errors.public_form_password">{{ err }}</span>
                                        </div>
                                    </div>
                                </div>

                                <div class="form-group row">
                                    <label class="col-lg-3 col-sm-4 col-form-label text-sm-right" :class="{'text-muted': !errors.require_employee_sig, 'text-danger': errors.require_employee_sig}">Require Signature:</label>
                                    <div class="col-sm-8 pt-2">
                                        <b-form-checkbox v-model="form.require_employee_sig" :value="1" :unchecked-value="0" plain>
                                            Require the Employee or Guest to Sign the Form
                                        </b-form-checkbox>

                                        <div class="text-danger mt-2" v-if="errors.require_employee_sig">
                                            <ul>
                                                <li v-for="err in errors.require_employee_sig">{{ err }}</li>
                                            </ul>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <personnel-edit ref="customFormPersonnelFields" :personnel="personnel" @personnelChaned="handlePersonnelChanged" />

                        <div class="alert alert-danger" v-if="errors.covid_fields">
                            <ul>
                                <li v-for="err in errors.covid_fields">{{ err }}</li>
                            </ul>
                        </div>

                        <custom-field-builder
                            ref="customFormCustomFieldBuilder"
                            :starting_sections="sections"
                            :personnel="assignablePersonnel"
                            @customFieldsSaved="saveCustomForm" />

                        <div class="mt-2">
                            <button type="button" class="btn btn-sm btn-success" v-on:click="saveCustomForm()">Save {{ form.covid_form_name }}</button>
                        </div>
                    </div>

                    <div class="col-lg-3 col-12">

                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
    import moment from 'moment-timezone';

    export default {

        data: function() {
            return {
                userTimezone: this.$store.state.user.timezone,
                id: this.$route.params.id,
                loading: true,
                saving: false,
                debug: this.$root.debug,
                errors: {},
                form: {},
                sections: [],
                originalSections: [],
                personnel: [],
            }
        },

        computed: {
            assignablePersonnel(){
                //Disabling, don't need it
                return [];

                //Loop and Build
                var assignable = [];

                for (var i = 0; i < this.personnel.length; i++)
                {
                    //ONLY Required, Employees can be assigned
                    // if(this.personnel[i].is_required === 1 && this.personnel[i].is_allow_other === 0)
                    // {
                    //     assignable.push(this.personnel[i]);
                    // }

                    //Push All
                    assignable.push(this.personnel[i]);
                }

                //Log and Send it
                return assignable;
            },
        },

        methods: {

            getCustomForm() {
                //Get the data
                this.$http
                    .get('/custom-forms/forms/edit' + (this.id ? '/' + this.id : ''))
                    .then(response => {
                        //Set all the data
                        this.form = response.data.form;
                        this.personnel = response.data.personnel;
                        this.sections = this.form.covid_fields ? this.form.covid_fields : [];
                        this.originalSections = JSON.parse(JSON.stringify(this.sections));
                    })
                    .catch(response => {
                        //Log it
                        console.log(response);
                    })
                    .then(() => {
                        //Set loading
                        this.loading = false;

                        //Check if sections are empty
                        if(this.sections.length === 0)
                        {
                            //Add Section
                            this.$refs.customFormCustomFieldBuilder.addSection();
                        }
                    });
            },

            saveCustomForm(sections = null){
                //SHow loading
                this.saving = true;

                //Reset the errors
                this.errors = {};

                //Setters
                this.form.covid_fields = sections != null ? sections : this.$refs.customFormCustomFieldBuilder.getSections();
                this.form.personnel = this.$refs.customFormPersonnelFields.getPersonnel();

                //Send it
                this.$http
                .post('/custom-forms/forms/edit', this.form)
                .then(response => {
                    //Alert the user?
                    this.$toastr.success('Custom Form Saved');

                    //Return to main list
                    this.$router.push('/custom-forms/forms/view/'+response.data.id);
                })
                .catch(error => {
                    //Check for error
                    if(error.response.status === 422)
                    {
                        //Display validation errors
                        this.errors = error.response.data
                    }
                    else
                    {
                        //Alert the user
                        this.$toastr.error(error.response.data, 'Failed to Save Custom Form');
                    }
                })
                .then(response => {
                    //SHow loading
                    this.saving = false;
                });
            },

            handlePersonnelChanged(personnel){
                //Set the Personnel
                this.personnel = personnel;
            },
        },

        mounted() {
            //Get the Incident TYpe
            this.getCustomForm();
        }
    }
</script>